<template>
  <div class="flex">
    <div v-if="props.type === TagType.PILL">
      <PrimeTag
        :pt="'pt_left' in props ? props.pt_left : undefined"
        :value="'label_left' in props ? props.label_left : undefined"
        :class="[
          unstyledClass
            ? ''
            : 'bg-primary-surface-lighter text-neutral-text-body',
          sizeClass,
          'rounded-l-full',
        ]"
      >
        <slot v-if="$slots.default_left" name="default_left" />
      </PrimeTag>
      <PrimeTag
        :pt="'pt_right' in props ? props.pt_right : undefined"
        :value="'label_right' in props ? props.label_right : undefined"
        :class="[
          unstyledClass
            ? ''
            : 'bg-blue-400 text-neutral-text-negative-title',
          sizeClass,
          'rounded-r-full',
        ]"
      >
        <slot v-if="$slots.default_left" name="default_left" />
      </PrimeTag>
    </div>
    <PrimeTag
      v-else
      :pt="'pt' in props ? props.pt : undefined"
      :value="'label' in props ? props.label : undefined"
      class="meridia-tag radius-full"
      :unstyled="true"
      :class="[tagStateClass, unstyledClass, sizeClass]"
    >
      <slot v-if="$slots.default" />
      <template v-slot:icon>
        <slot v-if="$slots.customIcon" name="customIcon" />
        <MeridiaIcon
          v-else-if="'icon' in props && props.icon"
          :class="[iconOrderClass, iconClass]"
          :name="props.icon"
          type="material-icons-outlined"
        />
      </template>
    </PrimeTag>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import {
  TagType,
  TagIconPosition,
  TagStateRiskLevel,
  TagStateAdditional,
  TagProps,
  TagSize,
} from './types';
import PrimeTag from 'primevue/tag';
import MeridiaIcon from '../icon/MeridiaIcon.vue';

const props = defineProps<TagProps>();

defineSlots<{
  default: () => any;
  customIcon: () => any;
  default_left: () => any;
  default_right: () => any;
}>();

const unstyledClass = computed(() => {
  if (props.type === TagType.UNSTYLED) {
    return 'unstyled';
  }
  return '';
});

const iconOrderClass = computed(() => {
  if (props.type === TagType.PILL) {
    return '';
  }
  return 'icon_position' in props && props.icon_position === TagIconPosition.RIGHT ? 'icon-right' : '';
});

const iconClass = computed(() => {
  if (props.type === TagType.PILL) {
    return '';
  }
  return 'icon-size';
});

const sizeClass = computed(() => {
  const size = props.size || TagSize.BASE;

  switch (size) {
    case TagSize.SMALL:
      return 'tag-small';
    case TagSize.BASE:
    default:
      return 'tag-base';
  }
});

const tagStateClass = computed(() => {
  if (props.type === TagType.PILL || props.type === TagType.UNSTYLED) {
    return '';
  }

  if (!('state' in props) || !props.state) {
    return '';
  }

  switch (props.state) {
    // Risk Level
    case TagStateRiskLevel.N_A:
      return 'risk-na';
    case TagStateRiskLevel.LOW:
      return 'risk-low';
    case TagStateRiskLevel.MEDIUM:
      return 'risk-medium';
    case TagStateRiskLevel.HIGH:
      return 'risk-high';
    case TagStateRiskLevel.CRITICAL:
      return 'risk-critical';

    // Additional
    case TagStateAdditional.PINK:
      return 'additional-pink';
    case TagStateAdditional.FUCHSIA:
      return 'additional-fuchsia';
    case TagStateAdditional.VIOLET:
      return 'additional-violet';
    case TagStateAdditional.INDIGO:
      return 'additional-indigo';
    case TagStateAdditional.BLUE:
      return 'additional-blue';
    case TagStateAdditional.SKY:
      return 'additional-sky';
    case TagStateAdditional.CYAN:
      return 'additional-cyan';
    case TagStateAdditional.TEAL:
      return 'additional-teal';
    case TagStateAdditional.EMERALD:
      return 'additional-emerald';
    case TagStateAdditional.LIME:
      return 'additional-lime';
    case TagStateAdditional.SLATE:
      return 'additional-slate';
    case TagStateAdditional.DARK_SLATE:
      return 'additional-dark-slate';
    default:
      return '';
  }
});
</script>

<style lang="scss" scoped>
.meridia-tag {
  @apply uppercase transition-all duration-200 inline-flex items-center gap-3xs;

  &.tag-base {
    @apply py-2xs px-m text-caption-small;
    .icon-size {
      @apply icon-xs;
    }
  }

  &.tag-small {
    @apply py-3xs px-s text-caption-small text-[9px];
    .icon-size {
      @apply text-[10px];
    }
  }

  &:not(.unstyled) {
    @apply text-neutral-text-body;
  }

  /* Icon */
  .icon-right {
    @apply order-1;
  }

  /* Risk Level classes */
  &.risk-na {
    @apply bg-neutral-border-default;
  }
  &.risk-low {
    @apply bg-primary-surface-subtle;
  }
  &.risk-medium {
    @apply bg-medium-surface-subtle;
  }
  &.risk-high {
    @apply bg-high-surface-subtle;
  }
  &.risk-critical {
    @apply bg-critical-surface-subtle;
  }

  /* Additional classes */
  &.additional-pink {
    @apply bg-pink-200;
  }

  &.additional-fuchsia {
    @apply bg-fuchsia-200;
  }

  &.additional-violet {
    @apply bg-violet-200;
  }

  &.additional-indigo {
    @apply bg-indigo-200;
  }

  &.additional-blue {
    @apply bg-blue-200;
  }

  &.additional-sky {
    @apply bg-sky-200;
  }

  &.additional-cyan {
    @apply bg-cyan-200;
  }

  &.additional-teal {
    @apply bg-teal-200;
  }

  &.additional-emerald {
    @apply bg-emerald-200;
  }

  &.additional-lime {
    @apply bg-lime-200;
  }

  &.additional-slate {
    @apply bg-slate-200;
  }

  &.additional-dark-slate {
    @apply bg-slate-500 text-white;
  }
}

// Pill tag size classes
.tag-base {
  &.rounded-l-full {
    @apply py-3xs pr-xs pl-m text-caption-small;
    .icon-size {
      @apply icon-xs;
    }
  }
  &.rounded-r-full {
    @apply py-3xs pl-xs pr-m text-caption-small;
    .icon-size {
      @apply icon-xs;
    }
  }
}

.tag-small {
  &.rounded-l-full {
    @apply py-3xs pr-xs pl-s text-caption-small text-[9px];
    .icon-size {
      @apply text-[10px];
    }
  }
  &.rounded-r-full {
    @apply py-3xs pl-xs pr-s text-caption-small text-[9px];
    .icon-size {
      @apply text-[10px];
    }
  }
}
</style>
